import { Flex } from 'antd';
import CardData from '../dashboard/cardData';
import { Divider } from '../Ant';
import InteractiveCard from '../common/interactiveCard';
import CardDetailButton from '../cardDetailButton/cardDetailButton';
import { useTranslation } from 'react-i18next';
import { Briefcase, Calendar, CalendarDate, Share, ShieldTick } from '../icons';
import { IMatter } from '../../interface/matter';
import {
  getConflictSearchMatterInformativeIcon,
  getTranslatedRoles,
} from '../../constants/conflictSearchUtility';
import { ICustomerType } from '../../constants/customer';
import { useParams } from 'react-router-dom';
import { IMatterParties } from '../../interface/matterParties';
import { useLanguage } from '../../context/LanguageContext';
import { MatterStatus } from '../../constants/matter';
import { BorderColor, TagColor } from '../../interface/common';
import { getLocalDateAndTime } from '../../constants/utilityFunctions';
import NoteIcons from '../common/NoteIcons';
import { MatterIndicators } from '../matters/matterIndicators';
import { InvoiceableType } from '../../constants/transaction';
import { ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';

const MatterCard = ({ matter, type }: { matter: IMatter; type: string }) => {
  const { t } = useTranslation();
  const { id: customerId } = useParams();
  const { getAppLanguageCode } = useLanguage();
  const { push } = useNavigationStack();
  const handleMatterDetail = () => {
    push(`${ROUTES.MATTER_DETAIL}/${matter.id}`);
  };
  return (
    <InteractiveCard
      data-testid="conflict-matter-card"
      className={`pointer w-full conflict-card ${matter?.status === MatterStatus.ACTIVE ? BorderColor.GREEN : BorderColor.RED}`}
      onActivate={handleMatterDetail}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={8} align="center">
          <Flex
            justify="center"
            align="center"
            className={`status-chip ${matter?.status === MatterStatus.ACTIVE ? TagColor.GREEN : TagColor.RED}`}
          >
            {t('matter:status.' + matter.status)}
          </Flex>
          <NoteIcons
            hasImportantNotes={matter?.hasImportantNotes}
            hasImportantRelatedNotes={matter?.hasImportantRelatedNotes}
            hasNotes={matter?.hasNotes}
          />
          {getConflictSearchMatterInformativeIcon({
            matterParty: matter?.parties?.find((party) =>
              type === ICustomerType.COMPANY
                ? party.companyId === customerId
                : party.privatePersonId === customerId,
            ) as IMatterParties,
            t,
          })}
          <MatterIndicators
            invoiceableType={matter?.invoiceableType as InvoiceableType}
            hasSubMatters={Boolean(matter?.hasSubMatters)}
            parentMatterId={matter?.parentMatterId}
            fixedPriceInUse={matter?.fixedPriceInUse}
            insider={matter?.insider}
            fixedPrice={matter?.fixedPrice}
            currency={matter?.currency}
          />
        </Flex>

        <Flex gap={10}>
          <CardDetailButton onOpen={handleMatterDetail} testId="conflict-matter-card-go-detail" />
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        <CardData
          type="matter"
          icon={<Briefcase />}
          title={t('matter:matter')}
          content={
            matter?.principalCompany?.name
              ? matter?.principalCompany?.name
              : (matter?.principalPrivatePerson?.fullName ?? '')
          }
          subcontent={matter?.matterNumber + ' - ' + matter?.subject}
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-1"
        />
        {matter?.parties && (
          <CardData
            icon={<Share />}
            type="transaction"
            title={t('matter:roles')}
            content={getTranslatedRoles(
              (
                matter?.parties?.find((party) =>
                  type === ICustomerType.COMPANY
                    ? party.companyId === customerId
                    : party.privatePersonId === customerId,
                ) as IMatterParties
              )?.roles ?? [],
              getAppLanguageCode(),
            )}
          />
        )}
        <Divider />
        <CardData
          icon={<ShieldTick />}
          type="transaction"
          title={t('matter:responsiblePerson')}
          content={matter?.responsibleUser?.fullName ?? '-'}
        />
        <Flex justify="space-between">
          <CardData
            type="price"
            icon={<Calendar />}
            title={t('matter:openingDate')}
            content={getLocalDateAndTime(new Date(matter?.openingDate ?? ''), true)}
          />
          <CardData
            icon={<CalendarDate />}
            title={t('matter:lastEntryDate')}
            content={getLocalDateAndTime(new Date(matter?.lastEntryDate ?? ''), true)}
          />
        </Flex>
      </Flex>
    </InteractiveCard>
  );
};

export default MatterCard;
